import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import { User, Mail, LogOut, Loader2 } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ProtectDashboardRoute from "../components/ProtectDashboardRoute";
import config from "../config";

export default function SettingsPage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch user details
  useEffect(() => {
    const fetchProfile = async () => {
      // 1. Start loading state explicitly
      setLoading(true);

      // 🔑 Step 1: Get the token from local storage
      const token = localStorage.getItem("authToken");

      if (!token) {
        // If no token exists, we can stop the request immediately.
        setProfile(null);
        setLoading(false); // Crucial: clear loading state
        return;
      }

      try {
        // 🚀 Step 2: Send the token in the Authorization header
        const res = await fetch(`${config.API_BASE_URL}/api/auth/profile`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            // Send the Bearer token
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();

        if (!res.ok) {
          console.error(
            `❌ Profile fetch failed (Status: ${res.status}):`,
            data.error || "Unknown error",
          );
          setProfile(null);
        } else if (data && !data.error && data._id) {
          // Check if data is valid and has no generic error indicator
          setProfile(data);
        } else {
          setProfile(null);
        }
      } catch (err) {
        console.error(
          "❌ Failed to fetch profile (Network/System Error):",
          err,
        );
        setProfile(null); // Clear profile state on critical failure
      } finally {
        // ✅ FIX: Ensure loading state is set to false here,
        // regardless of fetch success or failure.
        setLoading(false);
      }
    };

    fetchProfile();
    // Added navigate to dependencies if you choose to use it inside the fetchProfile (e.g., redirecting on 401)
  }, [navigate]);

  // Handle logout
  const handleLogout = async () => {
    setLogoutLoading(true);
    localStorage.removeItem("authToken"); // 🔑 Client-side: Remove the token first

    try {
      // 🚀 Server-side: Optionally notify server to invalidate session/refresh token (no cookies needed)
      // Note: If your backend logout doesn't require auth headers, this is fine.
      // If it requires the token, you'd need to re-add the Authorization header here too.
      await axios.post(
        `${config.API_BASE_URL}/api/auth/logout`,
        {},
        // ❌ REMOVED: { withCredentials: true }
      );
      // Success: Redirect to home page
      navigate("/");
    } catch (error) {
      console.error("❌ Logout error:", error);
      // Even if the server call fails, we assume the user is logged out
      // since the token is removed from localStorage.
      navigate("/");
    } finally {
      setLogoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <Loader2 className="w-6 h-6 animate-spin mr-3 text-[#007AFF]" />
        <p className="text-gray-600 text-lg font-medium">Loading profile...</p>
      </div>
    );
  }

  // Assuming you have components like DashboardLayout, Sidebar, MobileHeader imported/available
  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* ProtectDashboardRoute is required to check authentication status */}
      <ProtectDashboardRoute />
      <Sidebar />
      <MobileHeader />

      {/* Main Content */}
      <div className="flex-1 flex flex-col mt-14 md:mt-0">
        <main className="p-4 sm:p-8 flex-1">
          <div className="bg-white rounded-2xl shadow p-6 sm:p-8 max-w-2xl mx-auto space-y-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
              Account Settings
            </h2>

            {/* Profile Info */}
            {profile ? (
              <div className="space-y-6">
                {/* First Name */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    First Name
                  </label>
                  <div className="flex items-center px-4 py-3 rounded-lg border border-gray-300 bg-gray-100 text-gray-800">
                    <User className="w-5 h-5 mr-3 text-gray-500" />
                    {profile.firstName || "—"}
                  </div>
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Last Name
                  </label>
                  <div className="flex items-center px-4 py-3 rounded-lg border border-gray-300 bg-gray-100 text-gray-800">
                    <User className="w-5 h-5 mr-3 text-gray-500" />
                    {profile.lastName || "—"}
                  </div>
                </div>

                {/* Email Address */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Email Address
                  </label>
                  <div className="flex items-center px-4 py-3 rounded-lg border border-gray-300 bg-gray-100 text-gray-800">
                    <Mail className="w-5 h-5 mr-3 text-gray-500" />
                    {profile.email || "—"}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center text-red-500 font-medium p-4 border border-red-200 bg-red-50 rounded-lg">
                Could not load profile details. Please log in again.
              </p>
            )}

            {/* Logout Button */}
            <div className="pt-6 border-t">
              <button
                onClick={handleLogout}
                disabled={logoutLoading}
                className={`flex items-center justify-center gap-2 w-full ${
                  logoutLoading ? "bg-red-400" : "bg-red-500 hover:bg-red-600"
                } text-white px-6 py-3 rounded-lg shadow transition text-lg font-semibold disabled:cursor-not-allowed`}
              >
                {logoutLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <LogOut className="w-5 h-5" />
                )}
                {logoutLoading ? "Logging out..." : "Logout"}
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
